import { getCurrentLanguage } from "../shared/language";
import { getPhoneInputValidity, syncPhoneInput } from "./phone-input";

(function () {
  function validateEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function isSubmittableField(field) {
    if (!field) return false;
    if (field.tagName === "TEXTAREA" || field.tagName === "SELECT") return true;
    if (field.tagName !== "INPUT") return false;

    return !["hidden", "submit", "button", "reset", "image"].includes(field.type);
  }

  function initFormValidation(formScope) {
    if (!formScope || formScope.dataset.validationInit === "true") return;

    const form = formScope.querySelector("form.elem__form-wrapper");
    if (!form) return;

    formScope.dataset.validationInit = "true";
    form.noValidate = true;
    form.setAttribute("novalidate", "novalidate");

    const ERROR_MESSAGES = {
      en: {
        required: "This field is required.",
        email: "Enter a valid email address.",
        phone: "Enter a valid phone number.",
        checkbox: "Please check this box to continue.",
      },
      uk: {
        required: "Це поле є обов’язковим.",
        email: "Введіть коректну email адресу.",
        phone: "Введіть коректний номер телефону.",
        checkbox: "Будь ласка, поставте позначку, щоб продовжити.",
      },
      pl: {
        required: "To pole jest wymagane.",
        email: "Wpisz prawidłowy adres e-mail.",
        phone: "Wpisz prawidłowy numer telefonu.",
        checkbox: "Zaznacz to pole, aby kontynuować.",
      },
      de: {
        required: "Dieses Feld ist erforderlich.",
        email: "Geben Sie eine gültige E-Mail-Adresse ein.",
        phone: "Geben Sie eine gültige Telefonnummer ein.",
        checkbox: "Bitte aktivieren Sie dieses Kontrollkästchen, um fortzufahren.",
      },
      fr: {
        required: "Ce champ est obligatoire.",
        email: "Saisissez une adresse e-mail valide.",
        phone: "Saisissez un numéro de téléphone valide.",
        checkbox: "Veuillez cocher cette case pour continuer.",
      },
    };
    const getErrorMessages = () => ERROR_MESSAGES[getCurrentLanguage()] || ERROR_MESSAGES.en;
    const getFieldParent = (field) => field.closest(".elem__form-field");
    const getCheckboxWrapper = (checkbox) =>
      checkbox.closest(".elem__form-checkbox, .form_checkbox, .w-checkbox");
    const isRequiredField = (field) => field?.dataset.required === "true";
    const getFormControls = () => form.querySelectorAll("input, textarea, select");
    const getErrorMessage = (scope) => scope?.querySelector(".input__error-msg");
    const getErrorMessageText = (scope) => {
      const errorMessage = getErrorMessage(scope);
      return errorMessage?.querySelector(".input__error-msg-txt") || errorMessage;
    };
    const getCheckboxValidationScope = (checkbox) =>
      getFieldParent(checkbox) || getCheckboxWrapper(checkbox);

    const setValidationState = ({ field, scope, isValid, message = "" }) => {
      if (!scope) return;

      const hasError = !isValid;
      const errorMessage = getErrorMessage(scope);
      const errorMessageText = getErrorMessageText(scope);

      scope.toggleAttribute("data-has-error", hasError);
      scope.classList.toggle("is-valid", isValid);

      if (field) {
        field.setAttribute("aria-invalid", hasError ? "true" : "false");
      }

      if (!errorMessage) return;

      if (errorMessageText) {
        errorMessageText.textContent = message;
      }

      errorMessage.hidden = !hasError;
      errorMessage.setAttribute("aria-hidden", hasError ? "false" : "true");
    };

    const markFieldAsCustomRequired = (field) => {
      if (!isSubmittableField(field)) return;
      if (field.hasAttribute("required") || field.required) {
        field.dataset.required = "true";
      }
      if (field.dataset.required === "true") {
        field.setAttribute("aria-required", "true");
      }
      field.required = false;
      field.removeAttribute("required");
    };

    const syncRequiredState = () => {
      getFormControls().forEach(markFieldAsCustomRequired);
    };

    const getFormFields = () =>
      form.querySelectorAll(
        "input.elem__form-input:not([type='hidden']):not([type='submit']):not([type='checkbox']), textarea.elem__form-input, select.elem__form-input"
      );

    const getFormCheckboxes = () => form.querySelectorAll("input[type='checkbox']");

    syncRequiredState();
    getFormCheckboxes().forEach(markFieldAsCustomRequired);
    form.querySelectorAll(".input__error-msg").forEach((errorMessage) => {
      errorMessage.hidden = true;
      errorMessage.setAttribute("aria-hidden", "true");
    });

    function validateField(field) {
      const parent = getFieldParent(field);
      if (!parent) return true;

      const value = field.value.trim();
      const isRequired = isRequiredField(field);
      const isEmail = field.type === "email";
      const isPhone = field.type === "tel";
      const isOptional = parent.classList.contains("is-optional");

      parent.classList.add("is-touched");

      if (!isRequired && isOptional && value === "") {
        setValidationState({
          field,
          scope: parent,
          isValid: true,
        });
        parent.classList.remove("is-valid");
        return true;
      }

      if (isRequired && value === "") {
        setValidationState({
          field,
          scope: parent,
          isValid: false,
          message: getErrorMessages().required,
        });
        return false;
      }

      if (isEmail && value !== "" && !validateEmail(value)) {
        setValidationState({
          field,
          scope: parent,
          isValid: false,
          message: getErrorMessages().email,
        });
        return false;
      }

      if (isPhone && value !== "") {
        syncPhoneInput(field);
        const phoneValidity = getPhoneInputValidity(field);

        if (phoneValidity === false) {
          setValidationState({
            field,
            scope: parent,
            isValid: false,
            message: getErrorMessages().phone,
          });
          return false;
        }
      }

      setValidationState({
        field,
        scope: parent,
        isValid: true,
      });
      return true;
    }

    function validateCheckbox(checkbox) {
      const wrapper = getCheckboxWrapper(checkbox);
      const validationScope = getCheckboxValidationScope(checkbox);
      if (!checkbox || !wrapper || !formScope.contains(wrapper)) return true;

      if (!isRequiredField(checkbox)) {
        setValidationState({
          field: checkbox,
          scope: validationScope,
          isValid: true,
        });
        wrapper.classList.remove("is-error");
        wrapper.classList.remove("is-valid");
        return true;
      }

      wrapper.classList.add("is-touched");

      if (!checkbox.checked) {
        setValidationState({
          field: checkbox,
          scope: validationScope,
          isValid: false,
          message: getErrorMessages().checkbox,
        });
        wrapper.classList.add("is-error");
        wrapper.classList.remove("is-valid");
        return false;
      }

      setValidationState({
        field: checkbox,
        scope: validationScope,
        isValid: true,
      });
      wrapper.classList.remove("is-error");
      wrapper.classList.add("is-valid");
      return true;
    }

    function scrollToFirstError() {
      const firstError =
        form.querySelector(".elem__form-field[data-has-error]") ||
        form.querySelector(
          ".elem__form-checkbox[data-has-error], .form_checkbox[data-has-error], .w-checkbox[data-has-error]"
        );

      if (!firstError) return;

      firstError.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    form.addEventListener(
      "invalid",
      (e) => {
        e.preventDefault();
      },
      true
    );

    formScope.addEventListener(
      "click",
      (e) => {
        const submitTrigger = e.target.closest(
          "input[type='submit'], button[type='submit'], .elem__button-submit--abs, .elem__button-submit a, .elem__button-submit button"
        );
        if (!submitTrigger || !formScope.contains(submitTrigger)) return;

        syncRequiredState();
      },
      true
    );

    formScope.addEventListener(
      "keydown",
      (e) => {
        if (e.key !== "Enter") return;
        if (!form.contains(e.target) || e.target.tagName === "TEXTAREA") return;

        syncRequiredState();
      },
      true
    );

    formScope.addEventListener(
      "blur",
      (e) => {
        const field = e.target;
        if (
          !field.matches(
            "input.elem__form-input, textarea.elem__form-input, select.elem__form-input"
          )
        )
          return;
        if (field.type === "hidden" || field.type === "submit" || field.type === "checkbox") return;

        const parent = getFieldParent(field);
        const hasValue = field.value.trim() !== "";
        const isInvalid = parent?.hasAttribute("data-has-error");

        if (!hasValue && !isInvalid) return;

        validateField(field);
      },
      true
    );

    formScope.addEventListener("input", (e) => {
      const field = e.target;
      if (!field.matches("input.elem__form-input, textarea.elem__form-input")) return;
      if (field.type === "hidden" || field.type === "submit" || field.type === "checkbox") return;

      const parent = getFieldParent(field);
      if (parent?.classList.contains("is-touched")) {
        validateField(field);
      }
    });

    formScope.addEventListener("change", (e) => {
      const field = e.target;

      if (field.matches("select.elem__form-input")) {
        const parent = getFieldParent(field);
        if (parent?.classList.contains("is-touched")) {
          validateField(field);
        }
        return;
      }

      if (field.matches("input[type='checkbox']")) {
        validateCheckbox(field);
      }
    });

    form.addEventListener(
      "submit",
      (e) => {
        syncRequiredState();

        let isFormValid = true;

        getFormFields().forEach((field) => {
          if (!validateField(field)) {
            isFormValid = false;
          }
        });

        getFormCheckboxes().forEach((checkbox) => {
          if (!validateCheckbox(checkbox)) {
            isFormValid = false;
          }
        });

        if (isFormValid) return;

        e.preventDefault();
        e.stopPropagation();
        scrollToFirstError();
      },
      true
    );

    const requiredObserver = new MutationObserver(() => {
      syncRequiredState();
    });

    requiredObserver.observe(form, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["required"],
    });
  }

  function init() {
    document.querySelectorAll(".elem__form").forEach(initFormValidation);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
    return;
  }

  init();
})();
